import { Box } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { NavLink, Outlet } from "react-router-dom";

/**
 * AI Agent area sub-layout (mounted at `/marketing/agent`). The Marketing
 * section's primary area switch + the agent's operator controls live one
 * level up in `MarketingLayout`'s `<SectionHeader>`; this renders only the
 * agent's secondary sub-tab strip + the routed page. Keeping it a distinct,
 * lighter strip (vs. the section tabs) reads as the second nav level.
 *
 * The three ways to create content (autonomous drafting / idea generator /
 * on-demand drafting) used to be their own "Create New" + "Ideas" tabs; they
 * now live as the three tabs of the Content studio INSIDE the Workbench (next
 * to the approve queue + schedule calendar), so the strip no longer lists them
 * — `/marketing/agent/create` + `/ideas` redirect into the right Workbench tab.
 */
const SUB_TABS: { to: string; label: string; end: boolean }[] = [
  { to: "", label: "Workbench", end: true },
  { to: "schedule", label: "Schedule", end: false },
  { to: "status", label: "Status", end: false },
  { to: "knowledge", label: "Knowledge", end: false },
  { to: "history", label: "History", end: false },
  { to: "settings", label: "Settings", end: false },
];

/**
 * Secondary (in-content) tab — the app-wide segmented look (see
 * docs/ui-conventions.md → "Tabs") one size down from the section header's
 * primary row: the `<nav>` below is the recessed track, and the active link
 * (NavLink's auto `.active` class) is the lifted paper pill. Smaller + muted
 * so the two nav levels read distinctly. Local on purpose; the shared
 * `<SectionHeader>` owns the *primary* row only.
 */
const SubTabLink = styled(NavLink)(({ theme }) => ({
  position: "relative",
  display: "inline-flex",
  flexShrink: 0,
  alignItems: "center",
  minHeight: 30,
  padding: "5px 12px",
  borderRadius: Math.max(theme.shape.borderRadius - 2, 4),
  border: "1px solid transparent",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.2,
  textDecoration: "none",
  color: theme.palette.text.secondary,
  transition: theme.transitions.create(["color", "background-color", "box-shadow"], {
    duration: 150,
  }),
  "&:hover": {
    color: theme.palette.text.primary,
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
  "&.active": {
    color: theme.palette.primary.main,
    fontWeight: 600,
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.divider,
    boxShadow: theme.shadows[1],
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

export function MarketingAgentLayout(): JSX.Element {
  return (
    <>
      <Box
        component="nav"
        aria-label="Marketing agent sections"
        sx={{
          // The recessed TRACK of the segmented control (matches the theme's
          // MuiTabs track: surfaceSoft + hairline, radius knob + 2, 4px pad).
          mb: 2,
          display: "inline-flex",
          maxWidth: "100%",
          flexWrap: "nowrap",
          alignItems: "center",
          gap: "2px",
          p: "4px",
          borderRadius: (t) => `${t.shape.borderRadius + 2}px`,
          bgcolor: "surfaceSoft",
          border: 1,
          borderColor: "divider",
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {SUB_TABS.map((t) => (
          <SubTabLink key={t.to} to={t.to} end={t.end}>
            {t.label}
          </SubTabLink>
        ))}
      </Box>
      <Outlet />
    </>
  );
}
